import type {
  ActiveContractCompositionEntry,
  BasketSearchSnapshot,
  PortfolioSearchSnapshot,
  ProductSearchItem,
} from "../types/productV3";
import { isNewSnapshotShape } from "../types/productV3";

const CASH_CONID = 141175785;

export interface FlatPositionContribution {
  productName: string;
  productKey: string;
  effectiveWeight: number;
}

export interface StrategyFlatPosition {
  conId: number;
  netWeight: number;
  contributions: FlatPositionContribution[];
  isConflict: boolean;
  contractType: "STOCK" | "OPTION";
}

interface ProductWeightedContracts {
  productName: string;
  productKey: string;
  strategyWeight: number;
  stocks: ActiveContractCompositionEntry[];
  options: ActiveContractCompositionEntry[];
}

export interface StrategyPositionInput {
  entityType: "PORTFOLIO" | "BASKET";
  productId: number;
  weight: number;
  driftedWeight?: number | null;
  displayName: string;
  /**
   * Optional attribution override for an expanded parent product. When absent,
   * contributions keep the existing direct product key and display name.
   */
  sourceKey?: string;
  sourceName?: string;
}

function isFiniteWeight(weight: number | null | undefined): weight is number {
  return weight != null && Number.isFinite(weight);
}

export function resolveStrategyWeight(
  entry: Pick<StrategyPositionInput, "weight" | "driftedWeight">
): number {
  if (!isFiniteWeight(entry.driftedWeight)) return entry.weight;
  if (entry.weight < 0 && entry.driftedWeight > 0) return -entry.driftedWeight;
  return entry.driftedWeight;
}

export interface ProductDependencyIssue {
  code: "PRODUCT_SNAPSHOT_INCOMPLETE" | "NESTED_BASKET_NOT_FOUND";
  productKey: string;
  dependencyKey?: string;
}

export interface ProductContractsResult {
  stocks: ActiveContractCompositionEntry[];
  options: ActiveContractCompositionEntry[];
  complete: boolean;
  dependencyIssues: ProductDependencyIssue[];
}

export interface ProductContractExtractionOptions {
  /** Opt in to snapshot-shape, entry, and missing nested-basket validation. */
  strictDependencies?: boolean;
}

export interface StrategyDependencyIssue extends ProductDependencyIssue {
  sourceKey: string;
  sourceName: string;
}

export interface FlattenStrategyOptions {
  /** Existing callers remain permissive unless they explicitly request this. */
  strictDependencies?: boolean;
}

function resolveCompositionEntries<T extends { weight: number }>(
  activated: T[],
  performanceDrifts: T[],
  getId: (entry: T) => number,
  strictEntries = false
): T[] {
  const driftById = new Map<number, T>();
  for (const drift of performanceDrifts) {
    if (strictEntries) {
      if (!isValidCompositionEntry(drift, getId)) continue;
      driftById.set(getId(drift), drift);
    } else if (Number.isFinite(drift.weight)) {
      // Preserve the legacy permissive contract: only drift weights were
      // validated before strict Alpha dependency checks were introduced.
      driftById.set(getId(drift), drift);
    }
  }

  const resolved: T[] = [];
  const seen = new Set<number>();

  for (const activatedEntry of activated) {
    if (strictEntries && !isValidCompositionEntry(activatedEntry, getId)) continue;
    const id = getId(activatedEntry);
    resolved.push(driftById.get(id) ?? activatedEntry);
    seen.add(id);
  }

  for (const drift of performanceDrifts) {
    if (strictEntries && !isValidCompositionEntry(drift, getId)) continue;
    const id = getId(drift);
    if (!seen.has(id) && Number.isFinite(drift.weight)) {
      resolved.push(drift);
    }
  }

  return resolved;
}

function isValidCompositionEntry<T extends { weight: number }>(
  entry: T,
  getId: (entry: T) => number
): boolean {
  return (
    entry !== null &&
    typeof entry === "object" &&
    Number.isFinite(entry.weight) &&
    Number.isFinite(getId(entry))
  );
}

function hasValidCompositionEntries<T extends { weight: number }>(
  entries: T[] | undefined | null,
  getId: (entry: T) => number
): boolean {
  return Array.isArray(entries) && entries.every((entry) => (
    isValidCompositionEntry(entry, getId)
  ));
}

function arrayOrEmpty<T>(value: T[] | undefined | null): T[] {
  return Array.isArray(value) ? value : [];
}

function hasCompleteBasketSnapshotShape(snapshot: BasketSearchSnapshot): boolean {
  return (
    hasValidCompositionEntries(
      snapshot.activated?.contracts?.stocks,
      (stock) => stock.conId
    ) &&
    hasValidCompositionEntries(
      snapshot.performanceDrifts?.contracts?.stocks,
      (stock) => stock.conId
    )
  );
}

function hasCompletePortfolioSnapshotShape(snapshot: PortfolioSearchSnapshot): boolean {
  return (
    hasValidCompositionEntries(
      snapshot.activated?.contracts?.stocks,
      (stock) => stock.conId
    ) &&
    hasValidCompositionEntries(
      snapshot.activated?.contracts?.options,
      (option) => option.conId
    ) &&
    hasValidCompositionEntries(
      snapshot.activated?.baskets,
      (basket) => basket.basketId
    ) &&
    hasValidCompositionEntries(
      snapshot.performanceDrifts?.contracts?.stocks,
      (stock) => stock.conId
    ) &&
    hasValidCompositionEntries(
      snapshot.performanceDrifts?.contracts?.options,
      (option) => option.conId
    ) &&
    hasValidCompositionEntries(
      snapshot.performanceDrifts?.baskets,
      (basket) => basket.basketId
    )
  );
}

/**
 * Extracts the weighted contract list from a single product snapshot,
 * expanding nested baskets for portfolios.
 * All weights returned are product-internal (not scaled by strategy allocation).
 */
export function extractProductContracts(
  item: ProductSearchItem,
  basketLookup: Map<number, ProductSearchItem>,
  options: ProductContractExtractionOptions = {}
): ProductContractsResult {
  const dependencyIssues: ProductDependencyIssue[] = [];
  const productKey = `${item.productType}-${item.productId}`;
  const addSnapshotIssue = (key: string) => {
    if (!options.strictDependencies) return;
    dependencyIssues.push({
      code: "PRODUCT_SNAPSHOT_INCOMPLETE",
      productKey: key,
    });
  };

  if (!isNewSnapshotShape(item.snapshot)) {
    addSnapshotIssue(productKey);
    return {
      stocks: [],
      options: [],
      complete: dependencyIssues.length === 0,
      dependencyIssues,
    };
  }

  if (item.productType === "BASKET") {
    const snap = item.snapshot as BasketSearchSnapshot;
    if (snap.partial === true || !hasCompleteBasketSnapshotShape(snap)) {
      addSnapshotIssue(productKey);
    }
    return {
      stocks: resolveCompositionEntries(
        arrayOrEmpty(snap.activated?.contracts?.stocks),
        arrayOrEmpty(snap.performanceDrifts?.contracts?.stocks),
        (stock) => stock.conId,
        options.strictDependencies
      ),
      options: [],
      complete: dependencyIssues.length === 0,
      dependencyIssues,
    };
  }

  const snap = item.snapshot as PortfolioSearchSnapshot;
  if (snap.partial === true || !hasCompletePortfolioSnapshotShape(snap)) {
    addSnapshotIssue(productKey);
  }
  const directStocks = resolveCompositionEntries(
    arrayOrEmpty(snap.activated?.contracts?.stocks),
    arrayOrEmpty(snap.performanceDrifts?.contracts?.stocks),
    (stock) => stock.conId,
    options.strictDependencies
  );
  const directOptions = resolveCompositionEntries(
    arrayOrEmpty(snap.activated?.contracts?.options),
    arrayOrEmpty(snap.performanceDrifts?.contracts?.options),
    (option) => option.conId,
    options.strictDependencies
  );
  const basketRefs = resolveCompositionEntries(
    arrayOrEmpty(snap.activated?.baskets),
    arrayOrEmpty(snap.performanceDrifts?.baskets),
    (basket) => basket.basketId,
    options.strictDependencies
  );

  const expandedStocks: ActiveContractCompositionEntry[] = [...directStocks];

  for (const basketRef of basketRefs) {
    const basketItem = basketLookup.get(basketRef.basketId);
    const dependencyKey = `BASKET-${basketRef.basketId}`;
    if (
      !basketItem ||
      basketItem.productType !== "BASKET" ||
      !isNewSnapshotShape(basketItem.snapshot)
    ) {
      if (options.strictDependencies) {
        dependencyIssues.push({
          code: "NESTED_BASKET_NOT_FOUND",
          productKey,
          dependencyKey,
        });
      }
      continue;
    }
    const bSnap = basketItem.snapshot as BasketSearchSnapshot;
    if (bSnap.partial === true || !hasCompleteBasketSnapshotShape(bSnap)) {
      addSnapshotIssue(dependencyKey);
    }
    const basketStocks = resolveCompositionEntries(
      arrayOrEmpty(bSnap.activated?.contracts?.stocks),
      arrayOrEmpty(bSnap.performanceDrifts?.contracts?.stocks),
      (stock) => stock.conId,
      options.strictDependencies
    );
    for (const stock of basketStocks) {
      expandedStocks.push({
        ...stock,
        weight: (basketRef.weight * stock.weight) / 100,
      });
    }
  }

  return {
    stocks: expandedStocks,
    options: directOptions,
    complete: dependencyIssues.length === 0,
    dependencyIssues,
  };
}

/** Returns the current nested basket references used by the leaf flattener. */
export function getProductReferencedBasketIds(item: ProductSearchItem): number[] {
  if (item.productType !== "PORTFOLIO" || !isNewSnapshotShape(item.snapshot)) {
    return [];
  }
  const snap = item.snapshot as PortfolioSearchSnapshot;
  return resolveCompositionEntries(
    arrayOrEmpty(snap.activated?.baskets),
    arrayOrEmpty(snap.performanceDrifts?.baskets),
    (basket) => basket.basketId,
    true
  ).map((basket) => basket.basketId);
}

export interface FlattenResult {
  positions: StrategyFlatPosition[];
  missingProducts: string[];
  complete: boolean;
  dependencyIssues: StrategyDependencyIssue[];
}

/**
 * Flattens all strategy products into a single deduped list of positions,
 * aggregating across products by conId. Each position tracks which products
 * contribute to it and whether the position has conflicting directions.
 * Returns missing product names when a strategy entry has no matching product data.
 */
export function flattenStrategyPositions(
  strategyEntries: StrategyPositionInput[],
  productItems: ProductSearchItem[],
  basketLookup: Map<number, ProductSearchItem>,
  options: FlattenStrategyOptions = {}
): FlattenResult {
  const productItemMap = new Map<string, ProductSearchItem>();
  for (const item of productItems) {
    productItemMap.set(`${item.productType}-${item.productId}`, item);
  }

  const products: ProductWeightedContracts[] = [];
  const missingProducts: string[] = [];
  const dependencyIssues: StrategyDependencyIssue[] = [];
  for (const entry of strategyEntries) {
    const key = `${entry.entityType}-${entry.productId}`;
    const strategyWeight = resolveStrategyWeight(entry);
    if (strategyWeight === 0) continue;
    const sourceKey = entry.sourceKey ?? key;
    const sourceName = entry.sourceName ?? entry.displayName;
    const item = productItemMap.get(key);
    if (!item) {
      missingProducts.push(entry.displayName || key);
      continue;
    }
    const extracted = extractProductContracts(item, basketLookup, {
      strictDependencies: options.strictDependencies,
    });
    if (options.strictDependencies) {
      dependencyIssues.push(...extracted.dependencyIssues.map((issue) => ({
        ...issue,
        sourceKey,
        sourceName,
      })));
    }
    products.push({
      productName: sourceName,
      productKey: sourceKey,
      strategyWeight,
      stocks: extracted.stocks,
      options: extracted.options,
    });
  }

  const posMap = new Map<number, {
    conId: number;
    netWeight: number;
    contributions: FlatPositionContribution[];
    hasPositive: boolean;
    hasNegative: boolean;
    contractType: "STOCK" | "OPTION";
  }>();

  const addContribution = (
    conId: number,
    positionWeight: number,
    strategyWeight: number,
    productName: string,
    productKey: string,
    contractType: "STOCK" | "OPTION"
  ) => {
    if (conId === CASH_CONID) return;

    const effectiveWeight = (strategyWeight * positionWeight) / 100;

    let existing = posMap.get(conId);
    if (!existing) {
      existing = {
        conId,
        netWeight: 0,
        contributions: [],
        hasPositive: false,
        hasNegative: false,
        contractType,
      };
      posMap.set(conId, existing);
    }

    existing.netWeight += effectiveWeight;
    const existingContrib = existing.contributions.find((c) => c.productKey === productKey);
    if (existingContrib) {
      existingContrib.effectiveWeight += effectiveWeight;
    } else {
      existing.contributions.push({ productName, productKey, effectiveWeight });
    }

    if (effectiveWeight > 0) existing.hasPositive = true;
    if (effectiveWeight < 0) existing.hasNegative = true;
  };

  for (const product of products) {
    for (const stock of product.stocks) {
      addContribution(
        stock.conId,
        stock.weight,
        product.strategyWeight,
        product.productName,
        product.productKey,
        "STOCK"
      );
    }
    for (const option of product.options) {
      addContribution(
        option.conId,
        option.weight,
        product.strategyWeight,
        product.productName,
        product.productKey,
        "OPTION"
      );
    }
  }

  const result: StrategyFlatPosition[] = [];
  for (const [, entry] of posMap) {
    result.push({
      conId: entry.conId,
      netWeight: entry.netWeight,
      contributions: entry.contributions,
      isConflict: entry.hasPositive && entry.hasNegative,
      contractType: entry.contractType,
    });
  }

  result.sort((a, b) => Math.abs(b.netWeight) - Math.abs(a.netWeight));
  return {
    positions: result,
    missingProducts,
    complete: missingProducts.length === 0 && dependencyIssues.length === 0,
    dependencyIssues,
  };
}

export interface StrategyHoldingsSummary {
  uniquePositions: number;
  longCount: number;
  shortCount: number;
  optionCount: number;
}

export function computeHoldingsSummary(
  positions: StrategyFlatPosition[]
): StrategyHoldingsSummary {
  let longCount = 0;
  let shortCount = 0;
  let optionCount = 0;

  for (const pos of positions) {
    if (pos.contractType === "OPTION") {
      optionCount++;
    } else if (pos.netWeight > 0) {
      longCount++;
    } else if (pos.netWeight < 0) {
      shortCount++;
    }
  }

  return {
    uniquePositions: positions.length,
    longCount,
    shortCount,
    optionCount,
  };
}
